import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { 
  UserCog,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Mail,
  User,
  Shield,
  ShieldOff,
  Save,
  X
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle 
} from "../../components/ui/dialog";
import { AdminLayout } from "../../components/AdminLayout";
import { getAdminUsers, createAdminUser, updateAdminUser, deleteAdminUser } from "../../lib/adminApi";
import { toast } from "sonner";

export default function AdminUsersPage() {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({ email: "", name: "", password: "" });

  useEffect(() => { loadUsers(); }, []);

  const loadUsers = async () => {
    try {
      const response = await getAdminUsers();
      setUsers(response.data);
    } catch (error) {
      toast.error(t('adminUsers.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreate = () => {
    setEditingUser(null);
    setFormData({ email: "", name: "", password: "" });
    setShowModal(true);
  };

  const handleOpenEdit = (user) => {
    setEditingUser(user);
    setFormData({ email: user.email, name: user.name, password: "" });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingUser) {
        const payload = { email: formData.email, name: formData.name };
        if (formData.password) payload.password = formData.password;
        await updateAdminUser(editingUser.id, payload);
        toast.success(t('adminUsers.updated'));
      } else {
        await createAdminUser(formData);
        toast.success(t('adminUsers.created'));
      }
      setShowModal(false);
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('adminUsers.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (user) => {
    try {
      await updateAdminUser(user.id, { is_active: !user.is_active });
      toast.success(user.is_active ? t('adminUsers.deactivated') : t('adminUsers.activated'));
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('adminUsers.toggleError'));
    }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(t('adminUsers.confirmDelete', { name: user.name, email: user.email }))) return;
    try {
      await deleteAdminUser(user.id);
      toast.success(t('adminUsers.deleted'));
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('adminUsers.deleteError'));
    }
  };

  return (
    <AdminLayout>
      <div className="p-4 md:p-8" data-testid="admin-users-page">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-heading font-bold text-white">{t('adminUsers.title')}</h1>
            <p className="text-slate-400 mt-1">{t('adminUsers.subtitle')}</p>
          </div>
          <Button onClick={handleOpenCreate} className="bg-emerald-600 hover:bg-emerald-700" data-testid="create-admin-btn">
            <Plus className="h-4 w-4 mr-2" />
            {t('admin.newAdmin')}
          </Button>
        </motion.div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="h-8 w-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : users.length === 0 ? (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-16 bg-slate-800 border border-slate-700 rounded-lg">
            <UserCog className="h-16 w-16 text-slate-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-white mb-2">{t('adminUsers.noAdmins')}</h3>
          </motion.div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="space-y-3">
            {users.map((user, i) => (
              <motion.div
                key={user.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                className={`bg-slate-800 border rounded-lg p-5 flex items-center justify-between ${user.is_active ? "border-slate-700" : "border-red-500/30 opacity-60"}`}
                data-testid={`admin-user-row-${user.id}`}
              >
                <div className="flex items-center gap-4">
                  <div className={`w-11 h-11 rounded-full flex items-center justify-center ${user.is_active ? "bg-emerald-500/20" : "bg-red-500/20"}`}>
                    <UserCog className={`h-5 w-5 ${user.is_active ? "text-emerald-400" : "text-red-400"}`} />
                  </div>
                  <div>
                    <p className="font-semibold text-white">{user.name}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <Mail className="h-3.5 w-3.5 text-slate-500" />
                      <span className="text-sm text-slate-400">{user.email}</span>
                    </div>
                  </div>
                  {!user.is_active && (
                    <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-500/20 text-red-400">{t('adminUsers.deactivated')}</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleToggleActive(user)}
                    className="text-slate-400 hover:text-white hover:bg-slate-700"
                    data-testid={`toggle-active-${user.id}`}
                    title={user.is_active ? t('common.deactivate') : t('common.activate')}
                  >
                    {user.is_active ? <ShieldOff className="h-4 w-4" /> : <Shield className="h-4 w-4" />}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleOpenEdit(user)}
                    className="text-slate-400 hover:text-white hover:bg-slate-700"
                    data-testid={`edit-admin-${user.id}`}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(user)}
                    className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                    data-testid={`delete-admin-${user.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-md bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCog className="h-5 w-5 text-emerald-500" />
              {editingUser ? t('admin.editAdmin') : t('admin.newAdmin')}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-slate-300 text-sm">{t('common.name')}</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder={t('adminUsers.namePlaceholder')}
                className="bg-slate-700 border-slate-600 text-white mt-1"
                required
                data-testid="admin-user-name-input"
              />
            </div>
            <div>
              <Label className="text-slate-300 text-sm">{t('common.email')}</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                placeholder="email@exemple.com"
                className="bg-slate-700 border-slate-600 text-white mt-1"
                required
                data-testid="admin-user-email-input"
              />
            </div>
            <div>
              <Label className="text-slate-300 text-sm">
                {t('adminUsers.passwordLabel')} {editingUser && <span className="text-slate-500">{t('adminUsers.passwordKeepEmpty')}</span>}
              </Label>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                placeholder={editingUser ? t('admin.newPasswordOptional') : t('admin.passwordPlaceholder')}
                className="bg-slate-700 border-slate-600 text-white mt-1"
                required={!editingUser}
                minLength={6}
                data-testid="admin-user-password-input"
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="submit" disabled={saving} className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="admin-user-save-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                {editingUser ? t('common.update') : t('common.create')}
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="border-slate-600 text-slate-300 hover:bg-slate-700">
                {t('common.cancel')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
